import { useSelector } from '@/store'
import React from 'react'
import { shallowEqual } from 'react-redux'
import { useLocation, Redirect } from 'react-router-dom'
import { antRoutes } from '@/utils'
import { useTranslation } from 'react-i18next'

export type Tree<T, CKey extends string> = {
  [K in CKey]?: T[]
}
/**
 * 遍历一棵树，可通过callbackfn返回true来中断遍历并返回当前节点
 * @param tree
 */
export function forEachTree<T extends Tree<T, CKey>, CKey extends string = 'children'>(tree: T | T[], callbackfn: (value: T) => boolean | void, key?: CKey) {
  const k = key ?? ('children' as CKey)
  // 当前节点
  let res = null
  let list = null
  if (Array.isArray(tree)) list = tree
  else if (callbackfn(tree)) return tree
  else list = tree[k]

  if (list)
    list.find((t) => {
      res = forEachTree(t, callbackfn, k)
      return !!res
    })
  return res
}

interface AuthRedirectProps {
  /** 优先跳转路径 */
  firstPath?: string
}

/** 处理重定向，重定向第一个有权限的路由，需要在获取权限树后处理 */
export const withRedirect = <P extends Record<string, any>>(Component: React.FC<P>, authRedirectProps?: AuthRedirectProps) => {
  const AuthRedirect: React.FC<P> = (props) => {
    const resourceMap = useSelector((state) => state.auth.authResource || {}, shallowEqual)
    const { firstPath } = authRedirectProps || {}
    const { route } = props
    const { pathname } = useLocation()
    const { t } = useTranslation()

    // 获取有权限的菜单
    const authRoute = antRoutes(route.routes, t)

    let redirectPath = forEachTree(authRoute, (item) => item.path === firstPath) ? firstPath : (authRoute?.[0]?.path as string)

    if (pathname === '/') {
      // 如果完全菜单没权限，跳去无无权限页面
      if (!resourceMap || Object.keys(resourceMap)?.length === 0) {
        redirectPath = '/noAuth/simple'
      } else if (resourceMap['dataAnalysis']) {
        // 如果有数据看板的权限，跳总览页
        redirectPath = '/dashboard'
      } else if (resourceMap['sales:outbounds']) {
        // 如果有订单列表权限，则优先跳转到订单列表
        redirectPath = '/order/list'
      }
    }

    return pathname === route.path && redirectPath ? <Redirect to={redirectPath} /> : <Component {...props} />
  }

  AuthRedirect.displayName = 'AuthRedirect'
  return AuthRedirect
}
